import React, { useEffect, useState } from 'react';
import MatchScoreBar, { deriveBracket, MatchBracket } from './MatchScoreBar';

interface ComponentRow {
  key: string;
  label: string;
  score: number;
  weight: number;
  weighted: number;
  analysis: string;
  details?: Record<string, any>;
}

interface NormalizedAnalysis {
  overall_score: number;
  bracket: MatchBracket;
  summary: string;
  components: ComponentRow[];
  recommendations: Array<{ type: string; title: string; description: string }>;
}

export interface MatchScoreDetailsModalProps {
  analysis: unknown;
  onClose: () => void;
  matchScore?: number;
}

const LEGACY_LABELS: Record<string, string> = {
  skills: 'Skills',
  experience: 'Experience',
  location: 'Location',
  salary: 'Compensation',
  profile_completeness: 'Profile completeness',
};

const LEGACY_WEIGHTS_PCT: Record<string, number> = {
  skills: 40,
  experience: 25,
  location: 15,
  salary: 10,
  profile_completeness: 10,
};

function safeParse(raw: string): any | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function normalizeAnalysis(raw: unknown, fallbackScore?: number): NormalizedAnalysis | null {
  if (raw == null) {
    if (typeof fallbackScore === 'number') {
      return {
        overall_score: fallbackScore,
        bracket: deriveBracket(fallbackScore),
        summary: 'No detailed breakdown available for this score.',
        components: [],
        recommendations: [],
      };
    }
    return null;
  }

  let obj: any = raw;
  if (typeof raw === 'string') {
    obj = safeParse(raw);
    if (obj == null) {
      return fallbackScore != null
        ? {
            overall_score: fallbackScore,
            bracket: deriveBracket(fallbackScore),
            summary: 'Score details could not be parsed.',
            components: [],
            recommendations: [],
          }
        : null;
    }
  }

  if (typeof obj !== 'object' || obj === null) {
    return fallbackScore != null
      ? {
          overall_score: fallbackScore,
          bracket: deriveBracket(fallbackScore),
          summary: 'Score details unavailable.',
          components: [],
          recommendations: [],
        }
      : null;
  }

  // New shape: has `components` array.
  if (Array.isArray(obj.components)) {
    const overall = typeof obj.overall_score === 'number' ? obj.overall_score : (fallbackScore ?? 0);
    return {
      overall_score: overall,
      bracket: (obj.bracket as MatchBracket) || deriveBracket(overall),
      summary: obj.summary || '',
      components: obj.components,
      recommendations: Array.isArray(obj.recommendations) ? obj.recommendations : [],
    };
  }

  // Legacy shape: `score_components` is a dict keyed by name with score+weight+analysis.
  if (obj.score_components && typeof obj.score_components === 'object') {
    const components: ComponentRow[] = [];
    for (const [key, val] of Object.entries(obj.score_components)) {
      const v = val as any;
      const score = typeof v?.score === 'number' ? v.score : 0;
      const weightPct = LEGACY_WEIGHTS_PCT[key] ?? Math.round((v?.weight ?? 0) * 100);
      let analysisText = '';
      if (typeof v?.analysis === 'string') {
        // Some legacy analyses are themselves JSON-encoded dicts; pull out a sentence.
        const inner = safeParse(v.analysis);
        if (inner && typeof inner === 'object') {
          if (Array.isArray(inner.matched_skills) || inner.missing_mandatory) {
            const matched = Array.isArray(inner.matched_skills) ? inner.matched_skills.length : 0;
            const missingM = Array.isArray(inner.missing_mandatory) ? inner.missing_mandatory.length : 0;
            analysisText = `${matched} skill(s) matched${missingM ? `, ${missingM} mandatory missing` : ''}`;
          } else {
            analysisText = v.analysis;
          }
        } else {
          analysisText = v.analysis;
        }
      }
      components.push({
        key,
        label: LEGACY_LABELS[key] || key,
        score,
        weight: weightPct,
        weighted: Math.round(score * weightPct) / 100,
        analysis: analysisText,
        details: typeof v?.analysis === 'string' ? safeParse(v.analysis) || undefined : undefined,
      });
    }
    const overall = typeof obj.overall_score === 'number' ? obj.overall_score : (fallbackScore ?? 0);
    return {
      overall_score: overall,
      bracket: deriveBracket(overall),
      summary: obj.summary || '',
      components,
      recommendations: Array.isArray(obj.recommendations) ? obj.recommendations : [],
    };
  }

  // Unknown shape — fall back to score only.
  if (typeof fallbackScore === 'number') {
    return {
      overall_score: fallbackScore,
      bracket: deriveBracket(fallbackScore),
      summary: 'Detailed breakdown unavailable for this application.',
      components: [],
      recommendations: [],
    };
  }
  return null;
}

const RECO_COLORS: Record<string, { bg: string; fg: string }> = {
  critical: { bg: '#fee2e2', fg: '#b91c1c' },
  improvement: { bg: '#dbeafe', fg: '#1d4ed8' },
  info: { bg: '#e0e7ff', fg: '#4338ca' },
  consideration: { bg: '#fef3c7', fg: '#92400e' },
};

function componentColor(score: number): string {
  return ({
    excellent: '#16a34a',
    good: '#2563eb',
    moderate: '#d97706',
    limited: '#dc2626',
  } as const)[deriveBracket(score)];
}

const ComponentRowView: React.FC<{ row: ComponentRow }> = ({ row }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = !!row.details && Object.keys(row.details).length > 0;
  const color = componentColor(row.score);

  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', padding: '12px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>
            {row.label} <span style={{ color: '#64748b', fontWeight: 400, fontSize: 12 }}>· {row.weight}%</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <div
              style={{
                flex: 1,
                height: 6,
                background: '#e2e8f0',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.max(0, Math.min(100, row.score))}%`,
                  height: '100%',
                  background: color,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
            <div style={{ minWidth: 60, textAlign: 'right', color: color, fontWeight: 600, fontSize: 13 }}>
              {Math.round(row.score)} / 100
            </div>
          </div>
          {row.analysis && (
            <div style={{ marginTop: 6, fontSize: 13, color: '#475569' }}>{row.analysis}</div>
          )}
        </div>
        {hasDetails && (
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            aria-expanded={expanded}
            style={{
              background: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              padding: '4px 8px',
              cursor: 'pointer',
              color: '#475569',
              fontSize: 12,
            }}
          >
            {expanded ? '▴' : '▾'}
          </button>
        )}
      </div>
      {expanded && hasDetails && <DetailsBody details={row.details!} />}
    </div>
  );
};

const Chips: React.FC<{ items: string[]; tone: 'good' | 'bad' | 'neutral' }> = ({ items, tone }) => {
  const palette: Record<typeof tone, { bg: string; fg: string }> = {
    good: { bg: '#dcfce7', fg: '#166534' },
    bad: { bg: '#fee2e2', fg: '#991b1b' },
    neutral: { bg: '#e2e8f0', fg: '#334155' },
  };
  const p = palette[tone];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {items.map((i, idx) => (
        <span
          key={`${i}-${idx}`}
          style={{
            background: p.bg,
            color: p.fg,
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 12,
          }}
        >
          {i}
        </span>
      ))}
    </div>
  );
};

const DetailsBody: React.FC<{ details: Record<string, any> }> = ({ details }) => {
  return (
    <div style={{ marginTop: 10, padding: 12, background: '#f8fafc', borderRadius: 8, fontSize: 13 }}>
      {Array.isArray(details.matched_skills) && details.matched_skills.length > 0 && (
        <div>
          <div style={{ color: '#475569', marginBottom: 4 }}>Matched skills:</div>
          <Chips
            items={details.matched_skills.map((s: any) =>
              typeof s === 'string' ? s : `${s.skill} (lvl ${s.candidate_level}/${s.required_level})`
            )}
            tone="good"
          />
        </div>
      )}
      {Array.isArray(details.missing_mandatory) && details.missing_mandatory.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#475569', marginBottom: 4 }}>Missing mandatory:</div>
          <Chips items={details.missing_mandatory} tone="bad" />
        </div>
      )}
      {Array.isArray(details.missing_optional) && details.missing_optional.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#475569', marginBottom: 4 }}>Missing optional:</div>
          <Chips items={details.missing_optional} tone="neutral" />
        </div>
      )}
      {Array.isArray(details.skill_gaps) && details.skill_gaps.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#475569', marginBottom: 4 }}>Proficiency gaps:</div>
          <Chips items={details.skill_gaps.map((g: any) => `${g.skill} (+${g.gap} level)`)} tone="neutral" />
        </div>
      )}
      {Array.isArray(details.extra_relevant) && details.extra_relevant.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#475569', marginBottom: 4 }}>Extra relevant skills:</div>
          <Chips items={details.extra_relevant} tone="good" />
        </div>
      )}
      {Array.isArray(details.matched) && details.matched.length > 0 && (
        <div>
          <div style={{ color: '#475569', marginBottom: 4 }}>Matched keywords:</div>
          <Chips items={details.matched} tone="good" />
        </div>
      )}
      {Array.isArray(details.missing) && details.missing.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#475569', marginBottom: 4 }}>Missing keywords:</div>
          <Chips items={details.missing} tone="bad" />
        </div>
      )}
    </div>
  );
};

const MatchScoreDetailsModal: React.FC<MatchScoreDetailsModalProps> = ({ analysis, onClose, matchScore }) => {
  const data = normalizeAnalysis(analysis, matchScore);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!data) {
    return (
      <div
        onClick={onClose}
        style={overlayStyle}
      >
        <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
          <div style={{ padding: 24 }}>
            <h3 style={{ margin: 0 }}>Match details unavailable</h3>
            <p style={{ color: '#64748b' }}>No analysis was recorded for this application.</p>
            <button onClick={onClose} style={closeBtnStyle}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const bracketColor = ({
    excellent: '#16a34a',
    good: '#2563eb',
    moderate: '#d97706',
    limited: '#dc2626',
  } as const)[data.bracket];

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'transparent',
            border: 'none',
            fontSize: 22,
            cursor: 'pointer',
            color: '#64748b',
          }}
        >
          ×
        </button>
        <div style={{ padding: 24 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
            <MatchScoreBar score={data.overall_score} size="lg" bracket={data.bracket} />
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, color: '#0f172a' }}>AI Match Breakdown</h3>
              <div
                style={{
                  display: 'inline-block',
                  marginTop: 6,
                  padding: '3px 10px',
                  borderRadius: 999,
                  background: `${bracketColor}1a`,
                  color: bracketColor,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'capitalize',
                }}
              >
                {data.bracket}
              </div>
              {data.summary && (
                <div style={{ marginTop: 8, color: '#475569', fontSize: 14 }}>{data.summary}</div>
              )}
            </div>
          </div>

          {data.components.length > 0 ? (
            <div>
              <h4 style={{ margin: '8px 0 4px', color: '#0f172a', fontSize: 14 }}>Per-dimension breakdown</h4>
              {data.components.map((row) => (
                <ComponentRowView key={row.key} row={row} />
              ))}
            </div>
          ) : (
            <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, color: '#64748b', fontSize: 13 }}>
              No per-dimension breakdown was recorded for this application.
            </div>
          )}

          {data.recommendations.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ margin: '8px 0', color: '#0f172a', fontSize: 14 }}>Recommendations</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.recommendations.map((r, idx) => {
                  const palette = RECO_COLORS[r.type] || RECO_COLORS.info;
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: 12,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          background: palette.bg,
                          color: palette.fg,
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}
                      >
                        {r.type}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title}</div>
                        <div style={{ color: '#475569', fontSize: 13, marginTop: 2 }}>{r.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 640,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
};

const closeBtnStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '8px 16px',
  background: '#0f172a',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
};

export default MatchScoreDetailsModal;
